import React, { Component } from 'react';
import Styles from './addon-fullscreen.less';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import get from 'lodash/get';

declare global {
    interface HTMLDivElement {
        mozRequestFullScreen?: Function;
        msRequestFullscreen?: Function;
        webkitRequestFullScreen?: Function;
    }
    interface Document {
        webkitFullscreenElement: Element | null;
        msFullscreenElement: Element | null;
        webkitExitFullscreen?: Function;
        msExitFullscreen?: Function;
    }
}

interface AddonFullscreenConfig {
    iframeId?: string;
    embedUrl?: string;
    exitFullScreen?: () => void;
    title?: string;
}

interface AddonFullscreenState {
    show: boolean;
    config: Partial<AddonFullscreenConfig>;
}

class AddonFullscreen extends Component<{}, AddonFullscreenState> {
    private static fullScreenModal: AddonFullscreen | null = null;

    public static showFullScreen = (props: AddonFullscreenConfig): void => {
        if (!AddonFullscreen.fullScreenModal) {
            AddonFullscreen.fullScreenModal = AddonFullscreen.createFullScreen();
        }
        AddonFullscreen.fullScreenModal?.show(props);
    };

    private static createFullScreen = () => {
        const div = document.createElement('div');
        document.body.appendChild(div);
        // eslint-disable-next-line react/no-render-return-value
        return ReactDOM.render(<AddonFullscreen />, div) as unknown as AddonFullscreen;
    };

    private fullScreenRef: HTMLDivElement | null;

    public constructor(props: {}) {
        super(props);
        this.state = {
            show: false,
            config: {},
        };
        this.fullScreenRef = null;
    }

    public componentWillUnmount() {
        this.removeListener();
    }

    public render() {
        const iframeId = get(this.state, 'config.iframeId');
        const embedUrl = get(this.state, 'config.embedUrl');
        const title = get(this.state, 'config.title');

        return (
            <div
                className={classNames({
                    [Styles['addon-fullscreen-container']]: true,
                    [Styles['fullscreen-exit']]: !this.state.show,
                })}
                ref={ele => this.fullScreenRef = ele}
            >
                <div className={Styles['fullsreen-header']}>
                    <span className={Styles['back-fullscreen-title']}>{title}</span>
                    <div className={Styles['close-fullscreen']}
                        onMouseUp={this.exitFullScreen.bind(this)}
                        onTouchEnd={this.exitFullScreen.bind(this)}
                    >
                        <div className={Styles['back-fullscreen-icon']}/>
                        <div className={Styles['back-fullscreen-text']}>{i18n.t('返回文档')}</div>
                    </div>
                </div>
                <iframe id={iframeId}
                    src={embedUrl}
                    allowFullScreen
                    style={{
                        width: '100%',
                        height: 'calc(100% - 48px)',
                        backgroundColor: 'white',
                    }}
                />
            </div>
        );
    }

    private show = (props: AddonFullscreenConfig) => {
        const element = this.fullScreenRef;
        if (element) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            } else if (element.webkitRequestFullScreen) {
                element.webkitRequestFullScreen();
            }
            this.setState({
                show: true,
                config: props,
            });
            document.addEventListener('keydown', this.onExitKeyDown);
            document.addEventListener('fullscreenchange', this.handleFullScreenChange);
            document.addEventListener('mozfullscreenchange', this.handleFullScreenChange);
            document.addEventListener('webkitfullscreenchange', this.handleFullScreenChange);
            document.addEventListener('msfullscreenchange', this.handleFullScreenChange);
        } else {
            Snackbar.show({
                message: i18n.t('暂不支持该浏览器下全屏'),
                type: 'info',
                autoClose: true,
                duration: 1300,
            });
        }
    };

    private  handleFullScreenChange = () => {
        if (!(document.fullscreenElement /* Standard syntax */
            || document.webkitFullscreenElement /* Safari and Opera syntax */
            || document.msFullscreenElement)) {
            this.hide();
        }
    };

    private hide = () => {
        this.setState({
            show: false,
        });
        this.removeListener();
        AddonFullscreen.fullScreenModal = null;
    };

    private removeListener = () => {
        document.removeEventListener('keydown', this.onExitKeyDown);
        document.removeEventListener('fullscreenchange', this.handleFullScreenChange);
        document.removeEventListener('mozfullscreenchange', this.handleFullScreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullScreenChange);
    };

    private exitFullScreen = () => {
        if ((document.fullscreenElement /* Standard syntax */
            || document.webkitFullscreenElement /* Safari and Opera syntax */
            || document.msFullscreenElement)) { // typeError: Document not ready 全屏后点击跳转其他页，回来的时候浏览器会自动退出全屏
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { /* Safari */
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { /* IE11 */
                document.msExitFullscreen();
            }
        } else {
            AddonFullscreen.fullScreenModal?.hide();
        }
    };

    private onExitKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            AddonFullscreen.fullScreenModal?.hide();
        }
    };
}

export default AddonFullscreen;
